import type { ShopFeatureFlags } from "@/modules/merchant/domain/contracts/shop-feature-flags";
import { hasMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import {
    isMerchantNavCollapsible,
    merchantSidebarGroups,
    type MerchantNavCollapsibleItem,
    type MerchantNavItem,
    type MerchantNavSubItem,
} from "./data/merchant-sidebar-data";

export function subItemVisible(
  sub: MerchantNavSubItem,
  currentPermissions: string[],
  featureFlags: ShopFeatureFlags | null | undefined,
): boolean {
  if (
    sub.requiredShopFeature &&
    !hasShopFeature(featureFlags, sub.requiredShopFeature)
  ) {
    return false;
  }
  if (!sub.requiredMerchantPermission) return true;
  return hasMerchantPermission(currentPermissions, sub.requiredMerchantPermission);
}

export function filterVisibleNavItem(
  item: MerchantNavItem,
  currentPermissions: string[],
  featureFlags: ShopFeatureFlags | null | undefined,
): MerchantNavItem | null {
  if (isMerchantNavCollapsible(item)) {
    if (
      item.requiredShopFeature &&
      !hasShopFeature(featureFlags, item.requiredShopFeature)
    ) {
      return null;
    }
    if (
      item.requiredMerchantPermission &&
      !hasMerchantPermission(
        currentPermissions,
        item.requiredMerchantPermission,
      )
    ) {
      return null;
    }
    const subs = item.items.filter((s) =>
      subItemVisible(s, currentPermissions, featureFlags),
    );
    if (subs.length === 0) return null;
    return { ...item, items: subs };
  }
  if (
    item.requiredShopFeature &&
    !hasShopFeature(featureFlags, item.requiredShopFeature)
  ) {
    return null;
  }
  if (!item.requiredMerchantPermission) return item;
  return hasMerchantPermission(
    currentPermissions,
    item.requiredMerchantPermission,
  )
    ? item
    : null;
}

export type VisibleMerchantNavGroup = {
  title: string;
  items: MerchantNavItem[];
};

/** ກຸ່ມ sidebar ທີ່ເຫັນໄດ້ — ເຊື່ອງລາຍການ/feature flag ປິດ ແລະ ກຸ່ມວ່າງ */
export function getVisibleMerchantNavGroups(
  currentPermissions: string[],
  featureFlags: ShopFeatureFlags | null | undefined,
): VisibleMerchantNavGroup[] {
  return merchantSidebarGroups
    .map((group) => {
      const items = group.items
        .map((item) =>
          filterVisibleNavItem(item, currentPermissions, featureFlags),
        )
        .filter((item): item is MerchantNavItem => item != null);
      if (items.length === 0) return null;
      return { title: group.title, items };
    })
    .filter((g): g is VisibleMerchantNavGroup => g != null);
}

export function flattenNavLinks(
  groups: VisibleMerchantNavGroup[],
): { title: string; url: string }[] {
  const links: { title: string; url: string }[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (isMerchantNavCollapsible(item)) {
        for (const sub of item.items) {
          links.push({ title: sub.title, url: sub.url });
        }
      } else if ("url" in item) {
        links.push({ title: item.title, url: item.url });
      }
    }
  }
  return links;
}

/** URL ຫຼັກທີ່ມີໃນ bottom nav — ບໍ່ສະແດງໃນເມນູ «ອື່ນໆ» */
export const MOBILE_BOTTOM_NAV_URLS = new Set([
  "/app/shops/$shopId",
  "/app/shops/$shopId/pos",
  "/app/shops/$shopId/catalog",
  "/app/shops/$shopId/workshop",
]);

export function getMobileMoreNavLinks(
  groups: VisibleMerchantNavGroup[],
): { title: string; url: string }[] {
  return flattenNavLinks(groups).filter(
    (l) => !MOBILE_BOTTOM_NAV_URLS.has(l.url),
  );
}

export function isMerchantCollapsibleActive(
  href: string,
  item: MerchantNavCollapsibleItem,
  shopId: string,
): boolean {
  return item.items.some((s) => isMerchantNavLinkActive(href, s.url, shopId));
}

export function resolveShopNavUrl(pattern: string, shopId: string): string {
  return pattern.replace("$shopId", shopId);
}

export function isMerchantNavLinkActive(
  href: string,
  urlPattern: string,
  shopId: string,
): boolean {
  const path = resolveShopNavUrl(urlPattern, shopId);
  const h = (href ?? "").split("?")[0] ?? "";
  return h === path || h.startsWith(`${path}/`);
}
